// store/slices/categorySlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import apiClient from "@/app/utils/apiClient";

// =================== CATEGORY TYPES ==================
export interface Category {
  _id: string;
  name: string;
  isActive: boolean;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CategoryState {
  categories: Category[];
  loading: boolean;
  error: string | null;
  success: string | null;
}

const initialCategoryState: CategoryState = {
  categories: [],
  loading: false,
  error: null,
  success: null,
};

// ================== FETCH PUBLIC CATEGORIES (FOR USERS) ==================
export const fetchCategories = createAsyncThunk<
  Category[],
  void,
  { rejectValue: string }
>("categories/fetchCategories", async (_, { rejectWithValue }) => {
  try {
    // Public endpoint that returns all categories from all admins
    const response = await apiClient.get("/admin/categories");
    return response.data;
  } catch (err: any) {
    return rejectWithValue(
      err.response?.data?.message || "Failed to fetch categories",
    );
  }
});

const categorySlice = createSlice({
  name: "categories",
  initialState: initialCategoryState,
  reducers: {
    clearCategoryMessages: (state) => {
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Categories
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(
        fetchCategories.fulfilled,
        (state, action: PayloadAction<Category[]>) => {
          state.loading = false;
          state.categories = action.payload;
          state.success = "Categories fetched successfully";
        },
      )
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch categories";
      });
  },
});

export const { clearCategoryMessages } = categorySlice.actions;
export default categorySlice.reducer;
